import { toFiniteNumberOrNull } from '@/utils/transformer/number';

// NOTE: PREFIX_ID 依不同系統給予不同的前綴詞
const PREFIX_ID = 'hu-cms-';
const DEFAULT_EXPIRATION = new Date(9e12).getTime();

/**
 * 將 key 轉換為帶有前綴的儲存鍵值
 * @param key - 原始鍵值
 * @returns 帶前綴的鍵值
 */
const toExpirableStorageKey = (key: string) => PREFIX_ID + key;

// NOTE：
// 在某些裝置或瀏覽器，可能會被設定禁用 localStorage，
// 當使用 localStorage 時，
// 就會直接噴 Uncaught DOMException: The operation is insecure.
// 導致無法正常執行，故籍由此函式先進行測試。

/**
 * 檢查瀏覽器是否支援 localStorage
 * @type {boolean}
 */
const isLocalStorageSupported = (() => {
  try {
    const storage = window.localStorage;
    storage.getItem('hut-isLocalStorageSupported');
    return true;
  } catch {
    return false;
  }
})();

/**
 * 設定帶有過期時間的 localStorage 資料
 * @param key - 儲存鍵值
 * @param value - 要儲存的值
 * @param expired - 過期時間（毫秒），預設為遠未來時間
 */
const set = (key: string, value: unknown, expired = DEFAULT_EXPIRATION) => {
  const item = {
    value,
    expired,
  };

  localStorage.setItem(toExpirableStorageKey(key), JSON.stringify(item));
};

/**
 * 取得 localStorage 中的資料，自動處理過期邏輯
 * @param key - 儲存鍵值
 * @param defaultValue - 預設值
 * @returns 包含值和過期時間的物件或 null
 */
const get = (key: string, defaultValue: any = null) => {
  const storageItemRaw = localStorage.getItem(toExpirableStorageKey(key));

  // localStorage 無此項目，直接回傳 defaultValue
  if (!storageItemRaw) {
    return {
      value: defaultValue,
      expired: DEFAULT_EXPIRATION,
    };
  }

  try {
    const store = JSON.parse(storageItemRaw);
    const storeExpired = toFiniteNumberOrNull(store?.expired);

    // NOTE: localStorage 有取到值，但內容並無 expired 值，可能是和之前的 Key 撞名。
    if (storeExpired === null) {
      return {
        value: store,
        expired: DEFAULT_EXPIRATION,
      };
    }

    const now = Date.now();
    let storeValue = store?.value ?? null;

    if (now > storeExpired) {
      storeValue = null;
    }

    return {
      value: storeValue,
      expired: storeExpired,
    };
  } catch {
    return null;
  }
};

/**
 * 從 localStorage 中移除指定的資料
 * @param key - 要移除的鍵值
 */
const remove = (key: string) => {
  localStorage.removeItem(toExpirableStorageKey(key));
};

/**
 * 清除所有已過期的 localStorage 資料
 */
const clearExpired = () => {
  const now = Date.now();

  for (const key of Object.keys(localStorage)) {
    if (!key.startsWith(PREFIX_ID)) { continue; }

    const storageItemRaw = localStorage.getItem(key);
    if (storageItemRaw === null) { continue; }

    try {
      const store = JSON.parse(storageItemRaw);
      const storeExpired = toFiniteNumberOrNull(store?.expired);
      if (storeExpired === null) { continue; }

      if (now > storeExpired) {
        localStorage.removeItem(key);
      }
      // silent catch 是避免在 for 時中斷刪除使該清除的紀錄未被清除

    } catch {}
  }
};

/**
 * 可過期的 localStorage 操作介面
 */
export default {
  isLocalStorageSupported,
  set,
  get,
  remove,
  clearExpired,
};
